import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import Footer from '../../components/footer';
import SEO from '../../components/SEO';
import { useUser } from '../../context/UserContext';
import {
  CONVENIOS_DATA,
  CATEGORIAS_CONVENIOS,
  getConveniosByCategoria,
  Convenio
} from '../../data/convenios-data';
import {
  BENEFICIOS_NACIONALES,
  CATEGORIAS_NACIONALES,
  getBeneficiosByCategoria,
  BeneficioNacional
} from '../../data/beneficios-nacionales-data';

type TabType = 'locales' | 'nacionales';

export default function Beneficios() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('locales');
  const [categoriaLocal, setCategoriaLocal] = useState('todos');
  const [categoriaNacional, setCategoriaNacional] = useState('todos');
  const [expandedConvenio, setExpandedConvenio] = useState<string | null>(null);

  const conveniosFiltrados = getConveniosByCategoria(categoriaLocal);
  const beneficiosFiltrados = getBeneficiosByCategoria(categoriaNacional);

  const getCategoryColor = (categoria: string): string => {
    const colors: Record<string, string> = {
      educacion: '#6366f1',
      gastronomia: '#f59e0b',
      salud: '#10b981',
      servicios: '#3b82f6',
      experiencias: '#8b5cf6',
      seguros: '#ef4444',
      tecnologia: '#06b6d4',
      hospedaje: '#f97316',
      transporte: '#84cc16',
      inmobiliario: '#0ea5e9',
      eventos: '#ec4899',
      comercio: '#a855f7'
    };
    return colors[categoria] || '#6b7280';
  };

  const toggleConvenio = (id: string) => {
    setExpandedConvenio(expandedConvenio === id ? null : id);
  };

  const beneficiosSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Beneficios y Convenios AMPI Riviera Nayarit',
    description: 'Descubre los beneficios exclusivos y convenios para miembros AMPI. Más de 50 convenios locales y beneficios nacionales para profesionales inmobiliarios.',
    url: 'https://ampirivieranayarit.com/modulob/beneficios',
    provider: {
      '@type': 'Organization',
      name: 'AMPI Riviera Nayarit',
      url: 'https://ampirivieranayarit.com'
    }
  };

  return (
    <>
      <SEO
        title="Beneficios y Convenios para Miembros"
        description="Descubre los beneficios exclusivos y convenios para miembros AMPI. Más de 50 convenios locales en educación, gastronomía, salud y más. Beneficios nacionales en seguros, tecnología y hospedaje."
        path="/modulob/beneficios"
        keywords="beneficios ampi, convenios inmobiliarios, descuentos profesionales inmobiliarios, ampi riviera nayarit beneficios"
        schema={beneficiosSchema}
      />
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* Hero Section */}
      <section className="py-5" style={{ backgroundColor: '#0a2f27' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h1 className="text-white fw-bold mb-3">
                {t('beneficios.pageTitle')}
              </h1>
              <p className="text-white-50 fs-5 mb-4">
                {t('beneficios.pageSubtitle')}
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <span className="badge bg-light text-dark px-3 py-2">
                  <i className="bi bi-building me-2"></i>
                  {CONVENIOS_DATA.length} {t('beneficios.localPartners')}
                </span>
                <span className="badge bg-light text-dark px-3 py-2">
                  <i className="bi bi-globe me-2"></i>
                  {BENEFICIOS_NACIONALES.length} {t('beneficios.nationalBenefits')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-4 bg-light border-bottom">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="btn-group w-100" role="group">
                <button
                  type="button"
                  className={`btn btn-lg ${activeTab === 'locales' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('locales')}
                >
                  <i className="bi bi-geo-alt me-2"></i>
                  {t('beneficios.localTab')}
                </button>
                <button
                  type="button"
                  className={`btn btn-lg ${activeTab === 'nacionales' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveTab('nacionales')}
                >
                  <i className="bi bi-flag me-2"></i>
                  {t('beneficios.nationalTab')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-5">
        <div className="container">
          {/* Convenios Locales */}
          {activeTab === 'locales' && (
            <>
              {/* Filtros */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {CATEGORIAS_CONVENIOS.map((cat) => (
                      <button
                        key={cat.id}
                        className={`btn ${categoriaLocal === cat.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setCategoriaLocal(cat.id)}
                      >
                        {cat.label}
                        {cat.count && (
                          <span className="badge bg-light text-dark ms-2">{cat.count}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Grid de Convenios */}
              <div className="row g-4">
                {conveniosFiltrados.map((convenio: Convenio) => (
                  <div className="col-lg-6" key={convenio.id}>
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{ borderRadius: '12px', overflow: 'hidden' }}
                    >
                      {/* Header */}
                      <div
                        className="px-4 py-3 d-flex justify-content-between align-items-center"
                        style={{ backgroundColor: getCategoryColor(convenio.categoria) }}
                      >
                        <div>
                          <small className="text-white-50">{convenio.categoriaLabel}</small>
                          <h5 className="text-white mb-0 fw-bold">{convenio.nombreCorto}</h5>
                        </div>
                        {convenio.destacado && (
                          <span className="badge bg-warning text-dark">
                            <i className="bi bi-star-fill me-1"></i>
                            Destacado
                          </span>
                        )}
                      </div>

                      {/* Body */}
                      <div className="card-body p-4">
                        <p className="text-muted mb-3">{convenio.descripcion}</p>

                        {/* Acordeón de Beneficios */}
                        <div className="mb-3">
                          <button
                            className="btn btn-light w-100 d-flex justify-content-between align-items-center"
                            onClick={() => toggleConvenio(convenio.id)}
                          >
                            <span>
                              <i className="bi bi-gift me-2"></i>
                              {t('beneficios.viewBenefits')} ({convenio.beneficios.length})
                            </span>
                            <FiChevronDown
                              style={{
                                transform: expandedConvenio === convenio.id ? 'rotate(180deg)' : 'rotate(0)',
                                transition: 'transform 0.3s'
                              }}
                            />
                          </button>

                          {expandedConvenio === convenio.id && (
                            <div className="mt-3 p-3 bg-light rounded">
                              <ul className="list-unstyled mb-0">
                                {convenio.beneficios.map((beneficio, idx) => (
                                  <li key={idx} className="d-flex align-items-start mb-2">
                                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                                    <span>{beneficio}</span>
                                  </li>
                                ))}
                              </ul>

                              {convenio.condiciones.length > 0 && (
                                <div className="mt-3 pt-3 border-top">
                                  <small className="text-muted fw-bold">
                                    <i className="bi bi-info-circle me-1"></i>
                                    {t('beneficios.conditions')}:
                                  </small>
                                  <ul className="list-unstyled mb-0 mt-2">
                                    {convenio.condiciones.map((cond, idx) => (
                                      <li key={idx} className="text-muted small">
                                        <i className="bi bi-dot"></i> {cond}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Social Links */}
                        <div className="d-flex gap-2">
                          {convenio.redesSociales.facebook && convenio.redesSociales.facebook !== '#' && (
                            <a
                              href={convenio.redesSociales.facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              <i className="bi bi-facebook me-1"></i> Facebook
                            </a>
                          )}
                          {convenio.redesSociales.instagram && convenio.redesSociales.instagram !== '#' && (
                            <a
                              href={convenio.redesSociales.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-danger"
                            >
                              <i className="bi bi-instagram me-1"></i> Instagram
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Beneficios Nacionales */}
          {activeTab === 'nacionales' && (
            <>
              {/* Filtros */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {Object.values(CATEGORIAS_NACIONALES).map((cat) => (
                      <button
                        key={cat.id}
                        className={`btn ${categoriaNacional === cat.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setCategoriaNacional(cat.id)}
                      >
                        <i className={`bi ${cat.icon} me-1`}></i>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Grid de Beneficios Nacionales */}
              <div className="row g-4">
                {beneficiosFiltrados.map((beneficio: BeneficioNacional) => (
                  <div className="col-lg-4 col-md-6" key={beneficio.id}>
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{ borderRadius: '12px' }}
                    >
                      <div
                        className="card-header border-0 py-3"
                        style={{ backgroundColor: getCategoryColor(beneficio.categoria) }}
                      >
                        <small className="text-white-50">{beneficio.categoriaLabel}</small>
                        <h6 className="text-white mb-0 fw-bold">{beneficio.nombre}</h6>
                      </div>
                      <div className="card-body">
                        <p className="text-muted small mb-3">{beneficio.descripcion}</p>
                        <ul className="list-unstyled mb-0">
                          {beneficio.beneficios.map((item, idx) => (
                            <li key={idx} className="d-flex align-items-start mb-2">
                              <i className="bi bi-check2 text-success me-2 mt-1"></i>
                              <small>{item}</small>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h3 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
                {t('beneficios.ctaTitle')}
              </h3>
              <p className="text-muted mb-4">
                {t('beneficios.ctaSubtitle')}
              </p>
              <Link to="/list-with-us" className="btn btn-primary btn-lg px-5">
                {t('beneficios.ctaButton')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
